import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { fetchExpenses, createExpense, updateExpense, deleteExpense } from './expensesSlice';
import Shell from '../dashboard/Shell';
import RoleGuard from '../roles/RoleGuard';
import Modal from '../../components/ui/Modal';
import {
  Receipt, Plus, X, RefreshCw, Edit3, Trash2, ChevronLeft, ChevronRight,
  Loader2, ShieldCheck,
} from 'lucide-react';
import './Expenses.css';

const currency = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 });

const CATEGORIES = [
  { label: 'Marketing',             value: 'MARKETING',             color: '#8b5cf6' },
  { label: 'Rent',                  value: 'RENT',                  color: '#f59e0b' },
  { label: 'Utilities',             value: 'UTILITIES',             color: '#3b82f6' },
  { label: 'Salaries',              value: 'SALARIES',              color: '#10b981' },
  { label: 'Supplies',              value: 'SUPPLIES',              color: '#ec4899' },
  { label: 'Shipping',              value: 'SHIPPING',              color: '#06b6d4' },
  { label: 'Packaging',             value: 'PACKAGING',             color: '#f97316' },
  { label: 'Technology',            value: 'TECHNOLOGY',            color: '#6366f1' },
  { label: 'Professional Services', value: 'PROFESSIONAL_SERVICES', color: '#14b8a6' },
  { label: 'Insurance',             value: 'INSURANCE',             color: '#a855f7' },
  { label: 'Taxes',                 value: 'TAXES',                 color: '#ef4444' },
  { label: 'Maintenance',           value: 'MAINTENANCE',           color: '#84cc16' },
  { label: 'Inventory',             value: 'INVENTORY',             color: '#0ea5e9' },
  { label: 'Other',                 value: 'OTHER',                 color: '#64748b' },
];
const getCategoryInfo = (value) => CATEGORIES.find((c) => c.value === value) || { label: value, color: '#64748b' };

const ITEMS_PER_PAGE = 25;

const EMPTY_FORM = {
  category: 'MARKETING',
  amount: '',
  description: '',
  date: new Date().toISOString().split('T')[0],
};

export default function Expenses() {
  const dispatch = useDispatch();
  const { confirm } = useConfirm();
  const { isAuthenticated } = useSecureAuth();
  const { items, status, error } = useSelector((s) => s.expenses);

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [dateFilter, setDateFilter] = useState('month');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchExpenses());
  }, [isAuthenticated, dispatch]);

  useEffect(() => { setCurrentPage(1); }, [dateFilter, categoryFilter, searchTerm]);

  const handleRefresh = () => dispatch(fetchExpenses());

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    try {
      setSaving(true);
      const payload = {
        ...formData,
        amount: Number(formData.amount),
        date: new Date(formData.date),
      };
      if (editingExpense) {
        await dispatch(updateExpense({ id: editingExpense.id, expense: payload }));
      } else {
        await dispatch(createExpense({ expense: payload }));
      }
      handleCloseModal();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount.toString(),
      description: expense.description || '',
      date: new Date(expense.date).toISOString().split('T')[0],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm("This expense will be removed permanently.", {
      title: 'Delete expense?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (ok) await dispatch(deleteExpense(id));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setFormData(EMPTY_FORM);
  };

  const filteredExpenses = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    return items
      .filter((expense) => {
        const expDate = new Date(expense.date);
        const expDateOnly = new Date(expDate.getFullYear(), expDate.getMonth(), expDate.getDate());

        let dateMatch = true;
        switch (dateFilter) {
          case 'today': dateMatch = expDateOnly.getTime() === today.getTime(); break;
          case 'week':  dateMatch = expDateOnly >= weekAgo && expDateOnly <= today; break;
          case 'month': dateMatch = expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear(); break;
          case 'year':  dateMatch = expDate.getFullYear() === now.getFullYear(); break;
          default:      dateMatch = true;
        }

        if (categoryFilter !== 'all' && expense.category !== categoryFilter) return false;

        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          const inDesc = expense.description?.toLowerCase().includes(q);
          const inCat = getCategoryInfo(expense.category).label.toLowerCase().includes(q);
          if (!inDesc && !inCat) return false;
        }
        return dateMatch;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [items, dateFilter, categoryFilter, searchTerm]);

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const avgExpense = filteredExpenses.length > 0 ? totalExpenses / filteredExpenses.length : 0;

  const totalPages = Math.max(1, Math.ceil(filteredExpenses.length / ITEMS_PER_PAGE));
  const paginatedExpenses = filteredExpenses.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Shell>
      <RoleGuard
        permission="expenses:view"
        fallback={
          <div className="ex-page">
            <div className="ex-empty">
              <div className="ex-empty-icon"><ShieldCheck size={22} /></div>
              <h2 className="ex-empty-title">No access</h2>
              <p className="ex-empty-desc">You don't have permission to view expenses.</p>
            </div>
          </div>
        }
      >
        <div className="ex-page">

          {/* ── Header ───────────────────────────────────────────── */}
          <header className="ex-header">
            <div className="ex-header-text">
              <h1 className="ex-title">Expenses</h1>
              <p className="ex-subtitle">Track and manage business expenses.</p>
            </div>
            <div className="ex-header-actions">
              <button
                type="button"
                className="ex-btn ex-btn-ghost"
                onClick={handleRefresh}
                disabled={status === 'loading'}
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshCw size={14} className={status === 'loading' ? 'ex-spin' : ''} />
                <span className="ex-btn-text">Refresh</span>
              </button>
              <button
                type="button"
                className="ex-btn ex-btn-primary"
                onClick={() => setShowModal(true)}
              >
                <Plus size={14} /> <span className="ex-btn-text">Add expense</span>
              </button>
            </div>
          </header>

          {/* ── Banner ───────────────────────────────────────────── */}
          {error && (
            <div className="ex-banner ex-banner-error">
              <X size={16} />
              <span>{typeof error === 'string' ? error : 'Failed to load expenses'}</span>
            </div>
          )}

          {/* ── Stat tiles (3-up) ────────────────────────────────── */}
          <section className="ex-stat-grid">
            <div className="ex-stat">
              <span className="ex-stat-label">Total</span>
              <span className="ex-stat-value ex-stat-value-mono">{currency.format(totalExpenses)}</span>
            </div>
            <div className="ex-stat">
              <span className="ex-stat-label">Count</span>
              <span className="ex-stat-value">{filteredExpenses.length}</span>
            </div>
            <div className="ex-stat">
              <span className="ex-stat-label">Average</span>
              <span className="ex-stat-value ex-stat-value-mono">{currency.format(avgExpense)}</span>
            </div>
          </section>

          {/* ── Filters ──────────────────────────────────────────── */}
          <section className="ex-filters">
            <div className="ex-search">
              <input
                type="text"
                className="ex-input ex-search-input"
                placeholder="Search expenses…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="ex-search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              className="ex-input ex-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All time</option>
              <option value="today">Today</option>
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="year">This year</option>
            </select>
            <select
              className="ex-input ex-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </section>

          {/* ── Body ─────────────────────────────────────────────── */}
          {status === 'loading' ? (
            <div className="ex-loading">
              <Loader2 size={20} className="ex-spin" />
              <span>Loading expenses…</span>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="ex-empty">
              <div className="ex-empty-icon"><Receipt size={22} /></div>
              <h2 className="ex-empty-title">
                {items.length === 0 ? 'No expenses yet' : 'No matches'}
              </h2>
              <p className="ex-empty-desc">
                {items.length === 0
                  ? 'Add your first business expense to start tracking spend by category.'
                  : 'No expenses match your current filters.'}
              </p>
              {items.length === 0 ? (
                <button type="button" className="ex-btn ex-btn-primary" onClick={() => setShowModal(true)}>
                  <Plus size={14} /> Add your first expense
                </button>
              ) : (
                (searchTerm || categoryFilter !== 'all' || dateFilter !== 'month') && (
                  <button
                    type="button"
                    className="ex-btn ex-btn-secondary"
                    onClick={() => { setSearchTerm(''); setCategoryFilter('all'); setDateFilter('month'); }}
                  >
                    Clear filters
                  </button>
                )
              )}
            </div>
          ) : (
            <>
              <p className="ex-results">
                Showing {paginatedExpenses.length} of {filteredExpenses.length} expense{filteredExpenses.length === 1 ? '' : 's'}
              </p>
              <ul className="ex-list">
                {paginatedExpenses.map((expense) => {
                  const cat = getCategoryInfo(expense.category);
                  return (
                    <li key={expense.id} className="ex-row">
                      <div className="ex-row-main">
                        <span
                          className="ex-cat"
                          style={{ background: `${cat.color}1A`, color: cat.color }}
                        >
                          {cat.label}
                        </span>
                        <span className="ex-desc">{expense.description || '—'}</span>
                        <span className="ex-date">{formatDate(expense.date)}</span>
                        <span className="ex-amount">{currency.format(expense.amount)}</span>
                        <div className="ex-actions">
                          <button
                            type="button"
                            className="ex-icon-btn"
                            onClick={() => handleEdit(expense)}
                            aria-label="Edit expense"
                            title="Edit"
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            type="button"
                            className="ex-icon-btn ex-icon-btn-danger"
                            onClick={() => handleDelete(expense.id)}
                            aria-label="Delete expense"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <nav className="ex-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    className="ex-page-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="ex-page-info">
                    Page <strong>{currentPage}</strong> of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="ex-page-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </button>
                </nav>
              )}
            </>
          )}

          {/* ── Add / Edit modal ─────────────────────────────────── */}
          <Modal
            isOpen={showModal}
            onClose={handleCloseModal}
            title={editingExpense ? 'Edit expense' : 'Add expense'}
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="ex-btn ex-btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="ex-form"
                  className="ex-btn ex-btn-primary"
                  disabled={saving || !formData.amount}
                >
                  {saving ? <Loader2 size={14} className="ex-spin" /> : null}
                  {editingExpense ? 'Update' : 'Add expense'}
                </button>
              </>
            }
          >
            <form id="ex-form" onSubmit={handleSubmit} className="ex-form">
              <div className="ex-form-row">
                <div className="ex-field">
                  <label className="ex-field-label">Category</label>
                  <select
                    className="ex-input ex-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ex-field">
                  <label className="ex-field-label">Date</label>
                  <input
                    type="date"
                    className="ex-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="ex-field">
                <label className="ex-field-label">Amount (₦)</label>
                <input
                  type="number"
                  className="ex-input"
                  placeholder="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="ex-field">
                <label className="ex-field-label">Description (optional)</label>
                <textarea
                  className="ex-input ex-textarea"
                  placeholder="What was this expense for?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </form>
          </Modal>

        </div>
      </RoleGuard>
    </Shell>
  );
}
